import type { DocMigration } from "../docMigration";

function structuralWallTypeNew(
  name: string,
  structuralMaterialId: string,
  exteriorSubstrateMaterialId?: string,
  membraneMaterialId?: string,
  thermalMaterialId?: string,
): any {
  return {
    name,
    structuralMaterialId,
    exteriorSubstrateMaterialId,
    membraneMaterialId,
    thermalMaterialId,
  };
}

function wallMaterialNew(type: string, name: string, thickness: number): any {
  return {
    type,
    name,
    thickness,
  };
}

function structuralWallMaterialNew(lumberProfileType: string): any {
  const { name, width } = LumberProfileCatalog[lumberProfileType];
  return wallMaterialNew("STRUCTURE", name, width);
}

function lumberProfileDescriptorNew(type: string, name: string, width: number, height: number): any {
  return {
    type,
    name,
    width,
    height,
  };
}

const LumberProfileCatalog: { [key: string]: any } = {
  ["SPF_2X4"]: lumberProfileDescriptorNew("SPF_2X4", "2x4 SPF", 3.5, 1.5),
  ["SPF_2X6"]: lumberProfileDescriptorNew("SPF_2X6", "2x6 SPF", 5.5, 1.5),
  ["SPF_2X8"]: lumberProfileDescriptorNew("SPF_2X8", "2x8 SPF", 7.25, 1.5),
  ["SPF_2X10"]: lumberProfileDescriptorNew("SPF_2X10", "2x10 SPF", 9.25, 1.5),
  ["SPF_2X12"]: lumberProfileDescriptorNew("SPF_2X12", "2x12 SPF", 11.25, 1.5),
};

const wallMaterialIdFromLumberProfile: { [key: string]: string } = {
  ["SPF_2X4"]: "defaultStructuralMaterial2X4",
  ["SPF_2X6"]: "defaultStructuralMaterial2X6",
  ["SPF_2X8"]: "defaultStructuralMaterial2X8",
  ["SPF_2X10"]: "defaultStructuralMaterial2X10",
  ["SPF_2X12"]: "defaultStructuralMaterial2X12",
};

const defaultWallMaterialIdByType: { [key: string]: string } = {
  ["STRUCTURE"]: "defaultStructuralMaterial2X4",
  ["SUBSTRATE"]: "defaultSubstrateHalf",
  ["THERMAL"]: "defaultThermal",
  ["MEMBRANE"]: "defaultMembrane",
};

const defaultInteriorWallId = "WALL-INT-STD";
const defaultInteriorWallType = structuralWallTypeNew(
  defaultInteriorWallId,
  wallMaterialIdFromLumberProfile["SPF_2X4"],
);

const defaultExteriorWallId = "WALL-EXT-STD";
const defaultExteriorWallType = structuralWallTypeNew(
  defaultExteriorWallId,
  wallMaterialIdFromLumberProfile["SPF_2X4"],
  defaultWallMaterialIdByType["SUBSTRATE"],
);

const defaultWallTypes = {
  [defaultInteriorWallId]: defaultInteriorWallType,
  [defaultExteriorWallId]: defaultExteriorWallType,
};

function wallConditionDataNew(wallTypeId: string): any {
  return {
    wallTypeId,
  };
}

const defaultSubstrateHalf = wallMaterialNew("SUBSTRATE", `Substrate 1/2"`, 1 / 2);
const defaultSubstrateOne = wallMaterialNew("SUBSTRATE", `Substrate 1"`, 1);
const defaultThermal = wallMaterialNew("THERMAL", `Thermal`, 0);
const defaultMembrane = wallMaterialNew("MEMBRANE", `Membrane`, 0);
const defaultStructuralMaterial2X4 = structuralWallMaterialNew("SPF_2X4");
const defaultStructuralMaterial2X6 = structuralWallMaterialNew("SPF_2X6");
const defaultStructuralMaterial2X8 = structuralWallMaterialNew("SPF_2X8");
const defaultStructuralMaterial2X10 = structuralWallMaterialNew("SPF_2X10");
const defaultStructuralMaterial2X12 = structuralWallMaterialNew("SPF_2X12");

const defaultWallMaterials = {
  ["defaultSubstrateHalf"]: defaultSubstrateHalf,
  ["defaultSubstrateOne"]: defaultSubstrateOne,
  ["defaultThermal"]: defaultThermal,
  ["defaultMembrane"]: defaultMembrane,
  ["defaultStructuralMaterial2X4"]: defaultStructuralMaterial2X4,
  ["defaultStructuralMaterial2X6"]: defaultStructuralMaterial2X6,
  ["defaultStructuralMaterial2X8"]: defaultStructuralMaterial2X8,
  ["defaultStructuralMaterial2X10"]: defaultStructuralMaterial2X10,
  ["defaultStructuralMaterial2X12"]: defaultStructuralMaterial2X12,
};

const defaultWallConditions = {
  ["EXTERIOR_INTERIOR"]: wallConditionDataNew(defaultExteriorWallId),
  ["EXTERIOR_GARAGE"]: wallConditionDataNew(defaultExteriorWallId),
  ["EXTERIOR_PLUMBING"]: wallConditionDataNew(defaultExteriorWallId),
  ["EXTERIOR_DORMER"]: wallConditionDataNew(defaultExteriorWallId),
  ["EXTERIOR_GABLE_END"]: wallConditionDataNew(defaultExteriorWallId),
  ["INTERIOR_INTERIOR"]: wallConditionDataNew(defaultInteriorWallId),
  ["INTERIOR_GARAGE"]: wallConditionDataNew(defaultInteriorWallId),
  ["INTERIOR_PLUMBING"]: wallConditionDataNew(defaultInteriorWallId),
  ["INTERIOR_ROOF"]: wallConditionDataNew(defaultInteriorWallId),
  ["INTERIOR_FOUNDATION"]: wallConditionDataNew(defaultInteriorWallId),
};

const defaultWallCatalogSettings = {
  type: "WALL_CATALOG_SETTINGS",
  wallTypes: defaultWallTypes,
  wallMaterials: defaultWallMaterials,
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_79: DocMigration = {
  maxSchemaVersion: "0.0.79",
  description: "Fixing a bug at the wall type migration (empty wall types table)",
  apply(json: any) {
    const allSettings = json.settings;

    let validWallCatalog = false;
    for (const settingsId in allSettings) {
      const settings = allSettings[settingsId];
      if (settings.type !== "WALL_CATALOG_SETTINGS") {
        continue;
      }

      if (settings.wallTypes && Object.keys(settings.wallTypes).length > 0) {
        validWallCatalog = true;
        continue;
      }

      allSettings[settingsId] = defaultWallCatalogSettings;
    }

    if (!validWallCatalog) {
      for (const settingsId in allSettings) {
        const settings = allSettings[settingsId];
        if (settings.type !== "WALL_SETTINGS") {
          continue;
        }

        settings.wallConditions = defaultWallConditions;
      }
    }

    return json;
  },
};

export default migration_0_0_79;
